import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { SimpleFilterContent, type SimpleFilterField } from '@shared/components/ui/SimpleFilter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCouriersDropdown } from '../../hooks';
import { CourierFileFilters } from '../../models';

interface FiltersSectionProps {
  filters: CourierFileFilters;
  onFiltersChange: (filters: Partial<CourierFileFilters>) => void;
  onResetFilters: () => void;
}

export const FiltersSection = ({ filters, onFiltersChange, onResetFilters }: FiltersSectionProps) => {
  const { t } = useTranslation('codCourierFiles');
  const { couriers, isLoading: isLoadingCouriers } = useCouriersDropdown();

  // 🎯 Configuración de campos para SimpleFilter
  const fields: SimpleFilterField[] = useMemo(
    () => [
      {
        key: 'courier_id',
        label: t('filters.courier', 'Courier'),
        type: 'select',
        options: couriers.map((courier) => ({
          label: courier.name,
          value: String(courier.id),
        })),
        allOption: {
          label: t('filters.all_couriers', 'Todos los couriers'),
          value: 'all',
        },
        isLoadingOptions: isLoadingCouriers,
        placeholder: isLoadingCouriers
          ? t('filters.loading_couriers', 'Cargando...')
          : t('filters.select_courier', 'Seleccionar courier'),
        minWidth: '180px',
        maxWidth: '220px',
      },
      {
        key: 'date_from',
        label: t('filters.date_from', 'Fecha desde'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
      {
        key: 'date_to',
        label: t('filters.date_to', 'Fecha hasta'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
    ],
    [t, couriers, isLoadingCouriers],
  );

  // 🎯 Convertir valores de filtros a formato que SimpleFilter espera
  const filterValues = useMemo(() => {
    const values: Record<string, any> = {
      date_from: filters.date_from ? new Date(filters.date_from) : undefined,
      date_to: filters.date_to ? new Date(filters.date_to) : undefined,
      courier_id: filters.courier_id ? String(filters.courier_id) : 'all',
    };
    return values;
  }, [filters]);

  // 🎯 Handler para cambios en los filtros
  const handleFilterChange = (newValues: Partial<Record<string, any>>) => {
    const updatedFilters: Partial<CourierFileFilters> = {};

    // Convertir valores de vuelta al formato del modelo
    Object.keys(newValues).forEach((key) => {
      const value = newValues[key];

      if (key === 'courier_id') {
        updatedFilters.courier_id = value === 'all' || !value ? undefined : parseInt(value, 10);
      } else if (key === 'date_from' || key === 'date_to') {
        // Convertir Date a string formato 'YYYY-MM-DD' (equivalente a Y-m-d del API)
        if (value instanceof Date) {
          updatedFilters[key as 'date_from' | 'date_to'] = value.toLocaleDateString('en-CA'); // Produce formato YYYY-MM-DD
        } else if (value) {
          updatedFilters[key as 'date_from' | 'date_to'] = value;
        } else {
          updatedFilters[key as 'date_from' | 'date_to'] = undefined;
        }
      }
    });

    onFiltersChange(updatedFilters);
  };

  const activeFiltersCount = useMemo(
    () =>
      fields.filter((field) => {
        const value = filterValues[field.key];
        if (value === undefined || value === null || value === '') return false;
        if (field.type === 'select' && field.allOption && value === field.allOption.value) return false;
        return true;
      }).length,
    [fields, filterValues],
  );

  return (
    <div>
      <FiltersDropdown
        title={t('filters.title', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
        dropdownContentClassName="sm:min-w-[500px] sm:max-w-[500px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <SimpleFilterContent
          fields={fields}
          values={filterValues}
          onChange={handleFilterChange}
          onReset={onResetFilters}
          showClearButton={true}
          clearButtonText={t('filters.clear', 'Limpiar')}
          translations={{
            searching: t('filters.searching', 'Buscando...'),
            selectDate: t('filters.select_date', 'Seleccionar fecha'),
            clear: t('filters.clear', 'Limpiar'),
          }}
        />
      </FiltersDropdown>
    </div>
  );
};
